import { CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "../buttons/IconButton";
import AnchoredTooltip from "../overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../overlays/useAnchoredTooltip";
import { classNames } from "../styles/classNames";
import { useCopyToClipboard } from "./useCopyToClipboard";

interface CopyReferenceProps {
  /** The exact ID, hash or reference: copied on click and shown in the tooltip. */
  value: string;
  /** The accessible name, saying what is copied. "Copy reference" by default. */
  label?: string;
  className?: string;
}

/**
 * A small icon button in place of a visible ID or hash: readers see the name next to it,
 * and whoever cites copies the exact reference from here.
 */
export default function CopyReference({ value, label, className }: CopyReferenceProps) {
  const { t } = useTranslation();
  const { status, copy } = useCopyToClipboard();
  const descriptionId = useId();
  const tooltip = useAnchoredTooltip();
  const outcome = status === "idle" ? "" : t(`common.copyReference.${status}`);
  return (
    <span className={classNames("inline-flex shrink-0", className)}>
      <IconButton
        label={label ?? t("common.copyReference.label")}
        aria-describedby={descriptionId}
        onClick={() => { copy(value); }}
        {...tooltip.hoverProps()}
        {...tooltip.focusProps()}
        className="h-6 w-6 p-1"
      >
        {status === "copied" ? <CheckIcon className="h-3.5 w-3.5" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
      </IconButton>
      {/* The one place an exact ID is meant to be read (F102 CP-02); the visible-ID check skips it. */}
      <span id={descriptionId} className="sr-only" data-copy-value="">{value}</span>
      <span className="sr-only" aria-live="polite">{outcome}</span>
      {tooltip.shown === null ? null : (
        <AnchoredTooltip anchor={tooltip.shown.anchor} className="app-copy-reference-tooltip font-mono">
          {outcome === "" ? value : outcome}
        </AnchoredTooltip>
      )}
    </span>
  );
}
